import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { ApiError } from '@/services/awsAmplify/api'
import { UserInfo } from '@/services/user'
import { useFeed } from '@/lib/hooks/useFeed'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { useCreateTransaction } from '@/lib/hooks/wallet'

import { QueryKeys } from '@/enums'

import { PaymentForm } from '../PaymentForm'
import { Summary } from '../Summary'

type PaymentModalProps = {
  amount: number
  userInfo: UserInfo
  dismissModal: () => void
  translation: Record<string, string>
  paymentType: 'subscription'
  actionCallBack?: () => void
}

export const PaymentModal: FC<PaymentModalProps> = ({
  amount,
  dismissModal,
  userInfo,
  translation,
  paymentType,
  actionCallBack,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const {
    createTransactionData,
    createTransaction,
    error: unlockPaymentError,
    isLoadingTransactionData,
    resetTransactionData,
  } = useCreateTransaction({ userId: userInfo?.id })

  const { data: authUser } = useUserInfo()

  const computedUserId = useMemo(
    () => userInfo?.id || authUser?.id,
    [authUser?.id, userInfo?.id]
  )

  const { error, refetchFirstPage: refetchFirstSubscriptionPage } = useFeed({
    feedGroup: 'subscription',
    feedId: computedUserId,
    cacheKey: [QueryKeys.SubscriptionFeed, computedUserId],
    enabled: false,
    ranking: undefined,
    userId: computedUserId,
  })

  const status = (error as unknown as ApiError)?.response?.status

  const handleDismissModal = useCallback(() => {
    resetTransactionData()
    dismissModal()
  }, [dismissModal, resetTransactionData])

  const handleStart = useCallback(() => {
    handleDismissModal()
    actionCallBack?.()
  }, [handleDismissModal, actionCallBack])

  useEffect(() => {
    let interval = null
    if (createTransactionData) {
      interval = setInterval(() => {
        refetchFirstSubscriptionPage()
      }, 1000)
    }

    if (status !== 402) {
      clearInterval(interval)
    }
    return () => clearInterval(interval)
  }, [createTransactionData, refetchFirstSubscriptionPage, status])

  useEffect(() => {
    if (isLoadingTransactionData) {
      setIsLoading(true)
    }
  }, [isLoadingTransactionData])

  return (
    <div className="w-full flex flex-col items-center text-center sm:w-3/4 px-8 pt-5 pb-5 sm:py-8">
      {createTransactionData && status !== 402 ? (
        <Summary
          unlockPaymentData={createTransactionData}
          unlockPaymentError={unlockPaymentError}
          handleDismissModal={handleDismissModal}
          handleStart={handleStart}
          translation={translation}
        />
      ) : (
        <PaymentForm
          amount={amount}
          unlockPayment={() =>
            createTransaction({ value: String(amount), type: paymentType })
          }
          isLoadingUnlockPaymentData={isLoading}
          translation={translation}
          unlockPaymentError={unlockPaymentError}
        />
      )}
    </div>
  )
}
